import { Model } from 'infra/model'
import once from 'lodash/once'
import { computed, observable, makeObservable, action } from 'mobx'
import { Id } from 'types/definitions'
import { generateAvatarFallback } from 'utils/image-util'
import { UserDTO } from '../dtos/user'

export default class User extends Model<UserDTO, User> {
  id = Id.none
  platformAdmin = false
  publicId = Id.none
  authId = Id.none
  @observable givenName = ''
  @observable familyName = ''
  @observable picture = ''
  @observable online = false

  // NOTE: These will only be populated for the current user
  // or if the caller is a platform admin.
  @observable email = ''
  @observable phone = ''
  @observable multiFactorAuthenticationEnabled = false

  /**
   * Backing field for being fully loaded.
   *
   * @private
   */
  @observable private _isFullyLoaded = false

  /**
   * Whether the user has been fully loaded from IAM or if we only have
   * potentially stale properties.
   */
  @computed
  get isFullyLoaded() {
    return this._isFullyLoaded
  }

  /**
   * Default value to use for users.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * User constructor.
   *
   * @param getCurrentUserId
   */
  constructor(private getCurrentUserId: () => string | null) {
    super()
    makeObservable(this)
  }

  /**
   * Avatar.
   */
  @computed
  get avatar() {
    return this.picture || this.avatarFallback
  }

  /**
   * Avatar fallback.
   */
  @computed
  get avatarFallback() {
    return generateAvatarFallback(this.displayName || this.email)
  }

  /**
   * Whether this is the current user.
   */
  @computed
  get isCurrentUser() {
    return Id.some(this.id) && this.id === this.getCurrentUserId()
  }

  /**
   * Display name.
   */
  @computed
  get displayName() {
    return `${this.givenName} ${this.familyName}`
  }

  /**
   * Marks the user as fully loaded.
   */
  markFullyLoaded() {
    this._isFullyLoaded = true
  }

  /**
   * Marks the user as having Multi-factor Authentication enabled.
   */
  @action
  markMultiFactorAuthEnabled() {
    this.multiFactorAuthenticationEnabled = true
  }

  /**
   * Parse props onto the instance.
   *
   * @param json
   */
  parse(json: Partial<UserDTO>): Partial<User> {
    return {
      email: json.email,
      id: json.user_id,
      publicId: json.public_id,
      authId: json.auth_id,
      givenName: json.given_name,
      familyName: json.family_name,
      phone: json.phone,
      picture: json.picture,
      platformAdmin: json.platform_admin,
    }
  }
}

const lazyNone = once(() => new User(() => null))
